import React, { useState, useEffect, useMemo, useCallback, useRef, type RefObject } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useSlotGroup, useCollectionBinding, RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';
import { ctaHoverCss } from '../utils/ctaHover';
import { CtaArrow } from './shared/CtaArrow';

// ============================================================================
// Types for collection-bound gallery images
// ============================================================================

interface OverviewGalleryImage {
  image: string;
  alt_text?: string;
  title?: string;
  description?: string;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const amenitiesLinkDef: SlotGroupDefinition = {
  label: 'Amenities Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Image Slider Sub-component
// ============================================================================

function GallerySlider({ photos }: { photos: OverviewGalleryImage[] }) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const count = photos.length;

  const goTo = useCallback((idx: number) => {
    if (isTransitioning || count <= 1) return;
    setIsTransitioning(true);
    setCurrent(idx);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, count]);

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (count <= 1) return;
    timerRef.current = setTimeout(next, 5000);
    return () => clearTimeout(timerRef.current);
  }, [current, count, next]);

  if (!count) return <div className="w-full h-full bg-[var(--vibe-color-surface)]" />;

  return (
    <div className="relative w-full h-full overflow-hidden rounded-sm group">
      {photos.map((photo, i) => (
        <VibesitesImage
          key={i}
          url={photo.image || ''}
          alt={photo.alt_text || photo.title || ''}
          sizes="(min-width: 1024px) 50vw, 100vw"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-600 ease-in-out ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}

      {/* Brand-tinted overlay at bottom for depth */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />

      {count > 1 && (
        <>
          {/* Prev / Next arrows */}
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Previous image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 text-[var(--vibe-color-text)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-white"
            aria-label="Next image"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
            </svg>
          </button>

          {/* Dots */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === current ? 'bg-white w-5' : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function PropertyOverview() {
  const description = useContentToken('propertyoverview.description');
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);
  const amenitiesLink = useSlotGroup('propertyoverview.amenities_link', amenitiesLinkDef);

  // Use useCollectionBinding instead of useGallery so the image slider
  // is editable in the Vibesites content editor (hide, reorder, add custom images).
  const galleryImages = useCollectionBinding<OverviewGalleryImage>('propertyoverview.gallery_images', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 12,
  });
  const sliderPhotos = useMemo(
    () => (galleryImages || []).filter((p) => p.image),
    [galleryImages],
  );

  const [descExpanded, setDescExpanded] = useState(false);

  useRevealStyles();
  const accentRef = useScrollReveal<HTMLDivElement>({ threshold: 0.2 });

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">

          {/* ============================================================ */}
          {/* Left Column: Text + Amenity Tabs                              */}
          {/* ============================================================ */}
          <div ref={accentRef as RefObject<HTMLDivElement>} className="reveal-fade-up">
            {/* Accent line draws in above the eyebrow */}
            <svg className="accent-line accent-line--delay-1 mb-4" style={{ '--line-length': '64', width: '64px', height: '3px' } as React.CSSProperties} viewBox="0 0 64 3" preserveAspectRatio="none">
              <line x1="0" y1="1.5" x2="64" y2="1.5" strokeWidth="2" />
            </svg>

            <RichTextContent
              path="propertyoverview.pre_title"
              label="Eyebrow Text"
              defaultValue="<p>Your Search Ends Here</p>"
              as="p"
              className="font-body text-xs sm:text-sm uppercase tracking-[0.2em] text-[var(--vibe-color-primary)] mb-3"
            />

            <RichTextContent
              path="propertyoverview.title"
              label="Title"
              defaultValue="<p>Experience the charm.</p>"
              as="h2"
              className="font-heading text-3xl sm:text-4xl md:text-[2.75rem] font-normal text-[var(--vibe-color-text)] mb-5 leading-[1.15]"
            />

            <div className="mb-10 max-w-lg">
              <div className={`overview-desc-wrap ${descExpanded ? 'overview-desc-wrap--open' : ''}`}>
                <RichTextContent
                  path="propertyoverview.description"
                  label="Description"
                  defaultValue="<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities. Discover the perfect blend of comfort, style, and convenience.</p>"
                  as="div"
                  className="font-body text-sm sm:text-base text-[var(--vibe-color-text-muted)] leading-relaxed"
                />
              </div>
              {description && String(description).length > 200 && (
                <button
                  onClick={() => setDescExpanded(!descExpanded)}
                  className="mt-2 font-body text-sm font-semibold text-[var(--vibe-color-text)] hover:underline transition-colors"
                >
                  {descExpanded ? (
                    <RichTextContent
                      path="propertyoverview.read_less_label"
                      label="Read Less Label"
                      defaultValue="<p>Read Less</p>"
                      as="span"
                    />
                  ) : (
                    <RichTextContent
                      path="propertyoverview.read_more_label"
                      label="Read More Label"
                      defaultValue="<p>Read More</p>"
                      as="span"
                    />
                  )}
                </button>
              )}
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3">
              <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
                <a
                  href={ctaLink.url || '/floor-plans'}
                  target={ctaLink._target || '_self'}
                  className="overview-cta-btn inline-flex items-center gap-2"
                >
                  <span>{ctaLink.label || 'See All Availability'}</span>
                  <CtaArrow />
                </a>
              </EditableSlotGroup>

              <EditableSlotGroup groupPath="propertyoverview.amenities_link" definition={amenitiesLinkDef}>
                <a
                  href={amenitiesLink.url || '/amenities'}
                  target={amenitiesLink._target || '_self'}
                  className="overview-cta-btn overview-cta-btn--outline inline-flex items-center gap-2"
                >
                  <span>{amenitiesLink.label || 'View All Amenities'}</span>
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* ============================================================ */}
          {/* Right Column: Gallery Slider with corner accents              */}
          {/* ============================================================ */}
          <div className="glow-trace-wrap relative w-full aspect-[4/3] lg:aspect-[3/4] lg:max-h-[640px] rounded-sm overflow-hidden">
            <GlowTrace delay={0.5} />
            <GallerySlider photos={sliderPhotos} />
          </div>
        </div>
      </div>

      <style>{`
        /* Description expand / collapse */
        .overview-desc-wrap {
          max-height: 9.6em;
          overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          -webkit-mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
          mask-image: linear-gradient(to bottom, black 60%, transparent 100%);
        }
        .overview-desc-wrap--open {
          max-height: 60em;
          -webkit-mask-image: none;
          mask-image: none;
        }

        /* CTA Button */
        .overview-cta-btn {
          padding: 0.875rem 2rem;
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          transition: all 0.3s ease;
        }
        ${ctaHoverCss('.overview-cta-btn:not(.overview-cta-btn--outline)', 'var(--vibe-color-secondary)')}
        .overview-cta-btn--outline {
          color: var(--vibe-color-secondary);
          background: transparent;
          border: 1.5px solid var(--vibe-color-secondary);
        }
        .overview-cta-btn--outline:hover {
          color: var(--vibe-color-text-on-secondary);
          background: var(--vibe-color-secondary);
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }

        /* Slider transition */
        .duration-600 {
          transition-duration: 600ms;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.pre_title': {
      label: 'Eyebrow Text',
      type: 'richtext',
      defaultValue: '<p>Your Search Ends Here</p>',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultField: 'property_home_hero_title',
      defaultValue: '<p>Experience the charm.</p>',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
      defaultValue: '<p>Welcome to our residential community featuring spacious layouts and thoughtful amenities.</p>',
    },
    'propertyoverview.read_more_label': {
      label: 'Read More Label',
      type: 'richtext',
      defaultValue: '<p>Read More</p>',
    },
    'propertyoverview.read_less_label': {
      label: 'Read Less Label',
      type: 'richtext',
      defaultValue: '<p>Read Less</p>',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': {
      label: 'CTA Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'See All Availability' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'propertyoverview.amenities_link': {
      label: 'Amenities Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'View All Amenities' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'propertyoverview.gallery_images': {
      label: 'Overview Slideshow Images',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      defaultLimit: 12,
    },
  },
};

(PropertyOverview as any).contentSchema = contentSchema;

export default PropertyOverview;
